/**
 * Paged fetch past the PostgREST max-rows cap.
 *
 * Supabase's PostgREST layer silently caps EVERY response at max-rows
 * (1000 on Supabase Cloud) — plain table selects and set-returning RPCs
 * alike. Any collection query issued without .range() therefore returns at
 * most 1000 rows with no error, which corrupts stat tiles, exports, and
 * batch processing built on "all matching rows" (see Phase 28 bill
 * reconciliation bug).
 *
 * Usage — the caller rebuilds the query per page and applies the range:
 *
 *   const rows = await fetchAllRows<CaseLog>((from, to) =>
 *     supabase
 *       .from("consultant_doctor_case_logs")
 *       .select("*")
 *       .gte("case_date", start)
 *       .order("case_date", { ascending: false })
 *       .order("id")                       // REQUIRED: unique tiebreaker
 *       .range(from, to)
 *   );
 *
 * REQUIREMENT: the query's ORDER BY must be fully deterministic (end with a
 * unique column such as id). Offset pages re-execute the query; without a
 * total order, ties can shuffle between pages and rows get duplicated or
 * dropped.
 */

export const POSTGREST_PAGE_SIZE = 1000;

const DEFAULT_MAX_PAGES = 50;

interface PageResult<T> {
  data: T[] | null;
  error: { message: string; code?: string } | null;
}

export async function fetchAllRows<T>(
  fetchPage: (from: number, to: number) => PromiseLike<PageResult<T>>,
  opts: { pageSize?: number; maxPages?: number; label?: string } = {}
): Promise<T[]> {
  const pageSize = opts.pageSize ?? POSTGREST_PAGE_SIZE;
  const maxPages = opts.maxPages ?? DEFAULT_MAX_PAGES;
  const rows: T[] = [];

  for (let page = 0; page < maxPages; page++) {
    const from = page * pageSize;
    const { data, error } = await fetchPage(from, from + pageSize - 1);
    if (error) throw error;
    const pageRows = data ?? [];
    rows.push(...pageRows);
    // A short page means the result set is exhausted (PostgREST fills full
    // pages until the last one).
    if (pageRows.length < pageSize) return rows;
  }

  console.warn(
    `[fetchAllRows${opts.label ? `:${opts.label}` : ""}] stopped at the ${
      maxPages * pageSize
    }-row safety backstop — results are incomplete; narrow the filters.`
  );
  return rows;
}
